import React, { useMemo, useRef, useState, useCallback, Suspense } from 'react';
import { useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Innere Komponente die das Bild lädt und rendert
function UnderlayMesh({
  imageUrl,
  position,
  scale,
  rotation,
  opacity,
  draggable,
  onDrag,
  onDragEnd,
}) {
  const meshRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Textur laden
  const texture = useLoader(THREE.TextureLoader, imageUrl);

  // Plane-Dimensionen basierend auf Bild-Aspect-Ratio berechnen
  const dimensions = useMemo(() => {
    if (!texture.image) return { width: 10000, height: 10000 };
    const aspect = texture.image.width / texture.image.height;
    // Standard-Breite: 10m = 10000mm
    const baseWidth = 10000;
    return {
      width: baseWidth,
      height: baseWidth / aspect
    };
  }, [texture]);

  // Drag-Handler
  const handlePointerDown = useCallback((e) => {
    if (!draggable) return;
    e.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.point.x - position.x,
      y: e.point.y - position.y
    };
    e.target.setPointerCapture(e.pointerId);
  }, [draggable, position]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.point.x - dragStartRef.current.x;
    const newY = e.point.y - dragStartRef.current.y;
    onDrag?.({ x: newX, y: newY });
  }, [isDragging, onDrag]);

  const handlePointerUp = useCallback((e) => {
    if (!isDragging) return;
    setIsDragging(false);
    e.target.releasePointerCapture(e.pointerId);
    onDragEnd?.();
  }, [isDragging, onDragEnd]);

  return (
    <mesh
      ref={meshRef}
      position={[position.x, position.y, -1]} // Z=-1: hinter Grid (Grid ist bei Z=0-1)
      rotation={[0, 0, rotation * Math.PI / 180]}
      scale={[scale, scale, 1]}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <planeGeometry args={[dimensions.width, dimensions.height]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={opacity}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

// Fallback während das Bild lädt
function LoadingPlaceholder({ position, scale }) {
  return (
    <mesh position={[position.x, position.y, -1]} scale={[scale, scale, 1]}>
      <planeGeometry args={[5000, 5000]} />
      <meshBasicMaterial color="#e2e8f0" transparent opacity={0.3} />
    </mesh>
  );
}

// Haupt-Komponente mit Suspense für asynchrones Laden
export function ImageUnderlay3D({
  imageUrl,
  position = { x: 0, y: 0 },
  scale = 1,
  rotation = 0,
  opacity = 0.5,
  visible = true,
  draggable = false,
  locked = false, // Neu: Wenn locked=true, wird draggable ignoriert
  onDrag,
  onDragEnd,
}) {
  if (!visible || !imageUrl) return null;

  // Wenn locked, dann nicht verschiebbar (überschreibt draggable)
  const effectiveDraggable = draggable && !locked;

  return (
    <Suspense fallback={<LoadingPlaceholder position={position} scale={scale} />}>
      <UnderlayMesh
        imageUrl={imageUrl}
        position={position}
        scale={scale}
        rotation={rotation}
        opacity={opacity}
        draggable={effectiveDraggable}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
      />
    </Suspense>
  );
}
